import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import AntdFormItem from '../../../../../../../../components/AntdFormItem';
import styles from './index.module.scss';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import AntdInput from '../../../../../../../../components/AntdInput';
import { DatePicker, Form, Input, Switch, TimePicker } from 'antd';
import AntdSelect from '../../../../../../../../components/AntdSelect';
import AccionaSpinner from '../../../../../../../../components/AccionaSpinner';
import CompressiveStrengthInput from './CompressiveStrengthInput';

const FieldInput = ({
  labelKey,
  inputType,
  showManualValidation,
  disabled,
  name,
  options,
  rules,
  locked,
  validated,
  onClickLock,
  onClickValidate,
  loading,
  lockDisabled,
  selectedRegion,
  setSelectedRegion,
}) => {
  const intl = useIntl();
  const form = Form.useFormInstance();

  const fieldLabel = intl.formatMessage({ id: labelKey });

  const commonProps = {
    size: 'large',
    disabled: disabled || false,
    style: { width: '100%' },
  };

  const renderInputField = () => {
    switch (inputType) {
      case 'input':
        return <AntdInput {...commonProps} />;
      case 'timePicker':
        return <TimePicker {...commonProps} format="HH:mm" />;
      case 'datePicker':
        return <DatePicker {...commonProps} format="DD/MM/YYYY" />;
      case 'select':
        return <AntdSelect {...commonProps} options={options || []} />;
      case 'switch':
        return <Switch disabled={disabled || false} />;
      case 'textArea':
        return <Input.TextArea {...commonProps} rows={3} />;
      case 'compressiveStrengthInput':
        return (
          <CompressiveStrengthInput
            value={options?.value || []}
            onChange={options?.onChange}
            onAdd={options?.onAdd}
            onDelete={options?.onDelete}
            disabled={disabled}
            name={name}
          />
        );
      default:
        return <AntdInput {...commonProps} />;
    }
  };

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor:
          selectedRegion === name ? 'rgba(255, 107, 0, 0.25)' : 'transparent',
      }}
      onClick={() => {
        setSelectedRegion(name);
      }}
    >
      <span>
        {!lockDisabled && (
          <i
            className={`icon fas fa-${locked ? 'lock' : 'unlock-alt'} ${
              locked ? 'iconGreen' : 'iconRed'
            } fa-lg`}
            onClick={() => onClickLock(name)}
            style={{ cursor: 'pointer' }}
          />
        )}
      </span>
      {name !== 'compressive_strength' ? (
        <AntdFormItem
          name={name}
          label={fieldLabel}
          rules={rules}
          style={{ width: '100%' }}
        >
          {renderInputField()}
        </AntdFormItem>
      ) : (
        renderInputField()
      )}
      <span>
        {loading && <AccionaSpinner />}
        {!loading &&
          showManualValidation &&
          (validated ? (
            <CheckCircleFilled
              style={{ color: 'green', cursor: 'pointer' }}
              onClick={() => onClickValidate(name, !validated)}
            />
          ) : (
            <CloseCircleFilled
              style={{ color: 'red', cursor: 'pointer' }}
              onClick={() => {
                const canValidate = form.getFieldError(name).length === 0;
                if (canValidate) {
                  onClickValidate(name, !validated);
                }
              }}
            />
          ))}
      </span>
    </div>
  );
};

FieldInput.propTypes = {
  labelKey: PropTypes.string.isRequired,
  inputType: PropTypes.string.isRequired,
  showManualValidation: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  options: PropTypes.array,
  rules: PropTypes.array,
  locked: PropTypes.bool,
  validated: PropTypes.bool,
  onClickLock: PropTypes.func.isRequired,
  onClickValidate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  lockDisabled: PropTypes.bool,
  selectedRegion: PropTypes.string,
  setSelectedRegion: PropTypes.func.isRequired,
};

FieldInput.defaultProps = {
  project: null,
  showManualValidation: false,
  disabled: false,
  options: [],
  rules: [],
  locked: true,
  validated: false,
  loading: false,
  lockDisabled: false,
  selectedRegion: '',
};

export default FieldInput;
